import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, getCurrentInstance } from 'vue';
import flatPickr from 'vue-flatpickr-component';
import {
  add,
  format,
  DATE_TIME_FORMATS,
} from '@white-label-helper/date-utilities';
import IconButton from '@white-label-icon/icon-button';
import yearDropdownPlugin from './plugins/year-dropdown.ts';
import { useScreenSize } from '@white-label-helper/shared-composables';
// Types
import type { CustomLocale } from 'flatpickr/dist/types/locale.d.ts';

// flatpickr locales
import { Dutch as nl } from 'flatpickr/dist/l10n/nl.js';
import { english as en } from 'flatpickr/dist/l10n/default.js';
import { French as fr } from 'flatpickr/dist/l10n/fr.js';
import { German as de } from 'flatpickr/dist/l10n/de.js';
import { Italian as it } from 'flatpickr/dist/l10n/it.js';
import { Polish as pl } from 'flatpickr/dist/l10n/pl.js';
import { Portuguese as pt } from 'flatpickr/dist/l10n/pt.js';
import { Spanish as es } from 'flatpickr/dist/l10n/es.js';

type Config = {
  mode?: 'single' | 'multiple' | 'range';
  showMonths?: number;
  inline?: boolean;
  static?: boolean;
  minDate?: string | Date;
  maxDate?: string | Date;
  monthSelectorType?: 'static' | 'dropdown';
  locale?: CustomLocale;
};

type Locales = 'en' | 'fr' | 'es' | 'pl' | 'it' | 'de' | 'pt' | 'nl';

interface DatepickerProps {
  value?: string | string[];
  minDate?: Date;
  maxDate?: Date;
  timeZone?: string;
  isMobilePopUp?: boolean;
  mobilePopUpTitle?: string;
  label?: string;
  selectorType?: string;
  monthToShow?: number | null;
  useModelBinding?: boolean;
  selectionMode?: 'single' | 'range';
}


export default /*#__PURE__*/_defineComponent({
  __name: 'datepicker',
  props: {
    value: { default: '' },
    minDate: { default: () => new Date() },
    maxDate: { default: () => add(new Date(), { years: 1 }) },
    timeZone: { default: '' },
    isMobilePopUp: { type: Boolean, default: false },
    mobilePopUpTitle: { default: '' },
    label: { default: '' },
    selectorType: { default: 'static' },
    monthToShow: { default: null },
    useModelBinding: { type: Boolean, default: false },
    selectionMode: { default: 'single' }
  },
  emits: ["input", "on-change", "close"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'input', value: string | string[]): void;
  (e: 'on-change', value: string | string[]): void;
  (e: 'close'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: string | string[], minDate: Date, maxDate: Date, timeZone: string, isMobilePopUp: boolean, mobilePopUpTitle: string, label: string, selectorType: string, monthToShow: number | null, useModelBinding: boolean, selectionMode: 'single' | 'range' };

const proxy = getCurrentInstance()?.proxy as Vue;





const locales = ref({ fr, en, es, pl, it, de, pt, nl });

const { isMobileResolution } = useScreenSize();

const showMonths = computed<number>(() => {
  const desktopMonthToShow = props.monthToShow ?? 2;
  return desktopMonthToShow === 1 || isMobileResolution.value
    ? 1
    : desktopMonthToShow;
});

const twoLetterLanguageCode = computed<Locales>(
  () => `${proxy.$i18n.locale.slice(0, 2)}` as Locales
);

const computedLocales = computed(() => {
  const obj = {} as {
    [key: string]: CustomLocale;
  };

  Object.keys(locales.value).map((key) => {
    switch (key) {
      // Formatting for all english locales
      case 'en':
        obj[key] = {
          ...locales.value[key as Locales],
          weekdays: {
            shorthand: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
            longhand: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
          },
        };
        break;
      // Locales that use lowercase month formatting
      case 'fr':
      case 'es':
      case 'pl':
      case 'it':
      case 'pt':
      case 'nl':
        obj[key] = {
          ...locales.value[key as Locales],
          months: {
            shorthand: locales.value[key as Locales].months.shorthand,
            longhand: locales.value[key as Locales].months.longhand.map((item) =>
              item.toLowerCase()
            ),
          },
        };
        break;
      case 'de':
        obj[key] = locales.value[key as Locales];
        break;
    }
  });

  return obj;
});

const flatpickrConfig = computed<Config>(() => ({
  mode: props.selectionMode,
  defaultDate: props.value,
  showMonths: showMonths.value,
  inline: true,
  static: true,
  minDate: props.minDate,
  maxDate: props.maxDate,
  monthSelectorType: props.selectorType,
  locale: computedLocales.value[twoLetterLanguageCode.value],
  plugins: props.selectorType === 'dropdown' ? [yearDropdownPlugin()] : [],
}));

const localValue = computed(() => props.value);

const onChange = ($event: (number | Date)[]): void => {
  if (props.selectionMode === 'single') {
    const formattedDate = format($event[0], DATE_TIME_FORMATS.year_month_day);
    emit('input', formattedDate);
    emit('on-change', formattedDate);
  } else {
    const dates = $event.map((date) =>
      format(date, DATE_TIME_FORMATS.year_month_day)
    );
    emit('input', dates);
    emit('on-change', dates);
  }
}

return { __sfc: true,proxy, props, emit, locales, isMobileResolution, showMonths, twoLetterLanguageCode, computedLocales, flatpickrConfig, localValue, onChange, flatPickr, IconButton }
}

})